import { useState, useCallback, useRef, useEffect } from 'react';
import { RepairItem } from '../types/repairs';

export interface UseHomeRepairsOptions {
  homeIdOverride?: string;
  onSuccess?: (message: string) => void;
  onError?: (error: string) => void;
  showNotifications?: boolean;
}

export function useHomeRepairs(options?: UseHomeRepairsOptions) {
  const [repairs, setRepairs] = useState<RepairItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<number>(0);
  const homeId = options?.homeIdOverride || '';
  const showNotifications = options?.showNotifications ?? false;
  
  // Use refs for callback functions to avoid dependency changes
  const onSuccessRef = useRef(options?.onSuccess);
  const onErrorRef = useRef(options?.onError);
  
  // Update refs when callbacks change
  useEffect(() => {
    onSuccessRef.current = options?.onSuccess;
    onErrorRef.current = options?.onError;
  }, [options?.onSuccess, options?.onError]);

  // Fetch all repairs for a home
  const fetchRepairs = useCallback(async () => {
    if (!homeId) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await fetch(`/api/homes/${homeId}/repairs`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch repairs: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setRepairs(data.repairs || []);
        setLastUpdated(Date.now());
      } else {
        throw new Error(data.error || 'Failed to fetch repairs');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch repairs';
      setError(errorMessage);
      
      if (onErrorRef.current) {
        onErrorRef.current(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  }, [homeId, showNotifications]);

  // Get repairs summary statistics
  const getRepairsSummary = useCallback(() => {
    const totalCount = repairs.length;
    const unresolvedCount = repairs.filter(r => !r.is_resolved).length;
    const highUrgencyCount = repairs.filter(r => r.urgency === 'high' && !r.is_resolved).length;
    
    const totalMin = repairs.reduce((sum, repair) => sum + repair.cost_range[0], 0);
    const totalMax = repairs.reduce((sum, repair) => sum + repair.cost_range[1], 0);
    
    // Calculate average repair cost for unresolved repairs only
    const unresolvedRepairs = repairs.filter(r => !r.is_resolved);
    const unresolvedMin = unresolvedRepairs.reduce((sum, repair) => sum + repair.cost_range[0], 0);
    const unresolvedMax = unresolvedRepairs.reduce((sum, repair) => sum + repair.cost_range[1], 0);
    const averageRepairCost = unresolvedCount > 0 
      ? Math.round((unresolvedMin + unresolvedMax) / 2)
      : 0;
    
    return {
      totalCount,
      unresolvedCount,
      highUrgencyCount,
      costRange: { min: totalMin, max: totalMax },
      unresolvedCostRange: { min: unresolvedMin, max: unresolvedMax },
      averageRepairCost
    };
  }, [repairs]);

  // Filter repairs by various criteria
  const getRepairsByUrgency = useCallback((urgency: 'high' | 'medium' | 'low') => {
    return repairs.filter(repair => repair.urgency === urgency && !repair.is_resolved);
  }, [repairs]);

  const getRepairsByCategory = useCallback((category: string) => {
    return repairs.filter(repair => repair.category === category && !repair.is_resolved);
  }, [repairs]);

  const getRepairsByArea = useCallback((areaName: string) => {
    return repairs.filter(repair => repair.area_name === areaName && !repair.is_resolved);
  }, [repairs]);

  // Initial fetch on mount - using a ref to ensure it only runs once
  const initialFetchRef = useRef(false);
  
  useEffect(() => {
    if (homeId && !initialFetchRef.current) {
      initialFetchRef.current = true;
      fetchRepairs();
    }
  }, [homeId, fetchRepairs]);

  // Toggle repair resolution status with optimistic updates
  const toggleRepairResolution = useCallback(async (repairId: string) => {
    if (!homeId) return;
    
    // Find the repair to toggle
    const repair = repairs.find(r => r.id === repairId);
    if (!repair) return;
    
    const newResolvedStatus = !repair.is_resolved;
    
    try {
      // Optimistic update
      setRepairs(prevRepairs => 
        prevRepairs.map(r => 
          r.id === repairId 
            ? { ...r, is_resolved: newResolvedStatus, updated_at: new Date().toISOString() }
            : r
        )
      );
      
      const response = await fetch(`/api/homes/${homeId}/repairs/${repairId}/resolved/${newResolvedStatus}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update repair status: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to update repair status');
      }
      
      // Update with server response data
      setRepairs(prevRepairs => 
        prevRepairs.map(r => 
          r.id === repairId 
            ? { ...r, is_resolved: data.repair.is_resolved, updated_at: data.repair.updated_at }
            : r
        )
      );
      
      if (onSuccessRef.current && showNotifications) {
        onSuccessRef.current(`Repair marked as ${newResolvedStatus ? 'resolved' : 'unresolved'}`);
      }
      
    } catch (err) {
      // Revert optimistic update on error
      setRepairs(prevRepairs => 
        prevRepairs.map(r => 
          r.id === repairId 
            ? { ...r, is_resolved: repair.is_resolved, updated_at: repair.updated_at }
            : r
        )
      );
      
      const errorMessage = err instanceof Error ? err.message : 'Failed to update repair status';
      setError(errorMessage);
      
      if (onErrorRef.current) {
        onErrorRef.current(errorMessage);
      }
    }
  }, [homeId, repairs, showNotifications]);

  return {
    repairs,
    isLoading,
    error,
    fetchRepairs,
    getRepairsSummary,
    getRepairsByUrgency,
    getRepairsByCategory,
    getRepairsByArea,
    toggleRepairResolution,
    setError,
    lastUpdated
  };
}